import React, { useEffect, useRef } from "react";
import ss from "simple-ajax-uploader";
import { getCloudOrigin } from "../../helpers/cloudOrigin";

const defaultAllowed = ["jpg", "jpeg", "png", "gif"];

/**
 * Загрузка изображения на облако в формате FileUpload7 (SimpleAjaxUploader + /upload/file-upload7).
 * Без jQuery: элементы через refs.
 *
 * @param {object} props
 * @param {string} [props.cloudOrigin] — переопределение REACT_APP_FW_CLOUD_ORIGIN
 * @param {number} [props.maxSize] — лимит в КБ
 * @param {string[]} [props.allowedExtensions]
 * @param {string} [props.accept]
 * @param {object} [props.data] — доп. поля POST (в т.ч. update — JSON-строка)
 * @param {string} [props.controller] — префикс пути, по умолчанию upload
 * @param {boolean} [props.cors]
 * @param {boolean} [props.withCredentials]
 * @param {function} [props.onSuccess] — (response) => void при success === true
 * @param {function} [props.onError] — (message) => void
 * @param {string} [props.chooseFileLabel]
 * @param {string} [props.chooseAnotherLabel]
 * @param {string} [props.className]
 */
const FileUpload7Cloud = ({
  cloudOrigin: cloudOriginProp,
  maxSize = 5000,
  allowedExtensions = defaultAllowed,
  accept = "image/*",
  data = {},
  controller = "upload",
  cors = true,
  withCredentials = false,
  onSuccess,
  onError,
  chooseFileLabel = "Выбери файл",
  chooseAnotherLabel = "Другой файл",
  className = "",
}) => {
  const btnRef = useRef(null);
  const wrapRef = useRef(null);
  const picBoxRef = useRef(null);
  const errBoxRef = useRef(null);
  const uploaderRef = useRef(null);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  const server = (cloudOriginProp ?? getCloudOrigin()).replace(/\/$/, "");
  const dataKey = JSON.stringify(data);
  const extKey = allowedExtensions.join(",");

  useEffect(() => {
    if (!server || !btnRef.current || !wrapRef.current || !picBoxRef.current || !errBoxRef.current) {
      return undefined;
    }

    const btn = btnRef.current;
    const wrap = wrapRef.current;
    const picBox = picBoxRef.current;
    const errBox = errBoxRef.current;

    const SimpleUpload = ss.SimpleUpload;
    if (!SimpleUpload) {
      const msg = "SimpleAjaxUploader: конструктор SimpleUpload недоступен";
      if (onErrorRef.current) onErrorRef.current(msg);
      return undefined;
    }

    const uploader = new SimpleUpload({
      button: btn,
      url: `${server}/${controller}/file-upload7`,
      sessionProgressUrl: `${server}/${controller}/session-progress`,
      name: "imgfile",
      multiple: true,
      multipart: true,
      maxUploads: 2,
      maxSize,
      dropzone: btn,
      queue: false,
      allowedExtensions,
      data,
      accept,
      cors,
      withCredentials,
      debug: process.env.NODE_ENV === "development",
      hoverClass: "btn-hover",
      focusClass: "active",
      disabledClass: "disabled",
      responseType: "json",
      onSubmit() {
        const prog = document.createElement("div");
        const outer = document.createElement("div");
        const bar = document.createElement("div");
        const size = document.createElement("div");
        const self = this;

        prog.className = "prog";
        size.className = "size";
        outer.className = "progress progress-striped";
        bar.className = "progress-bar progress-bar-success";

        outer.appendChild(bar);
        prog.appendChild(size);
        prog.appendChild(outer);
        wrap.appendChild(prog);

        self.setProgressBar(bar);
        self.setProgressContainer(prog);
        self.setFileSizeBox(size);

        errBox.innerHTML = "";
        btn.value = chooseAnotherLabel;
      },
      onSizeError() {
        errBox.innerHTML = `Файл не должен превышать ${maxSize} КБ.`;
        if (onErrorRef.current) onErrorRef.current(`Превышен размер файла (${maxSize} КБ)`);
      },
      onExtError() {
        const msg = "Недопустимый тип файла. Выберите изображение JPG, PNG или GIF.";
        errBox.innerHTML = msg;
        if (onErrorRef.current) onErrorRef.current(msg);
      },
      onComplete(file, response) {
        if (!response) {
          const msg = "Не удалось загрузить файл.";
          errBox.innerHTML = msg;
          if (onErrorRef.current) onErrorRef.current(msg);
          return;
        }
        if (response.success === true) {
          if (onSuccessRef.current) onSuccessRef.current(response);

          picBox.innerHTML = `<img src="${response.url}" alt="" style="width:100%;max-width:200px" />`;
        } else {
          const msg =
            response.msg && String(response.msg)
              ? String(response.msg)
              : "Не удалось загрузить файл.";
          errBox.innerHTML = msg;
          if (onErrorRef.current) onErrorRef.current(msg);
        }
      },
    });

    uploaderRef.current = uploader;

    return () => {
      if (uploaderRef.current && typeof uploaderRef.current.destroy === "function") {
        uploaderRef.current.destroy();
      }
      uploaderRef.current = null;
      wrap.innerHTML = "";
    };
  }, [
    server,
    maxSize,
    extKey,
    accept,
    dataKey,
    controller,
    cors,
    withCredentials,
    chooseAnotherLabel,
  ]);

  if (!server) {
    return (
      <div className={`content-box text-danger ${className}`.trim()}>
        Не задан <code>REACT_APP_FW_CLOUD_ORIGIN</code>.
      </div>
    );
  }

  return (
    <div className={`content-box ${className}`.trim()}>
      <input
        ref={btnRef}
        type="button"
        className="btn btn-primary btn-large clearfix upload-btn buttonDragAndDrop"
        defaultValue={chooseFileLabel}
      />
      <span style={{ paddingLeft: 5, verticalAlign: "middle" }}>
        <i>
          {allowedExtensions.join(", ")} ({maxSize} Кб макс.)
        </i>
      </span>
      <div
        ref={errBoxRef}
        className="clearfix redtext errormsg"
        style={{ paddingTop: 10 }}
      />
      <div
        ref={wrapRef}
        className="progress-wrap pic-progress-wrap"
        style={{ marginTop: 10, marginBottom: 10 }}
      />
      <div
        ref={picBoxRef}
        data-id="picture"
        className="clear picbox"
        style={{
          paddingTop: 0,
          paddingBottom: 10,
          width: "100%",
          maxWidth: 200,
        }}
      />
      <div className="clear-line" style={{ marginTop: 10 }} />
    </div>
  );
};

export default FileUpload7Cloud;
